import { Component, OnInit, OnDestroy, HostListener } from '@angular/core';
import { Subject, filter, take, takeUntil } from 'rxjs';
import { MJGlobal, MJEventType, UUIDsEqual } from '@memberjunction/global';
import { StartupManager } from '@memberjunction/core';
import { IzzyMetadataEngine } from 'mj_generatedentities';
import { mjBizAppsCommonOrganizationEntity } from '@mj-biz-apps/common-entities';
import { BCSaaSChannelEntity } from '@bluecypress/bcsaas-entities';
import { IzzyContextService } from '../../services/izzy-context.service';

/**
 * izzy-global-context — the app-wide org/channel switcher.
 *
 * Rendered by the MJExplorer app component and physically relocated into the
 * MJ shell header (which has no projection slot), replacing the global search
 * bar. Writes IzzyContextService; every section (Messages, Tickets, Analytics,
 * Administration) already subscribes to orgChanges$/channelChanges$, so one
 * selection here drives the whole app. Also mirrors changes made by sections
 * back into its own display (the service's no-op setters prevent echo loops).
 */
@Component({
  standalone: false,
  selector: 'izzy-global-context',
  templateUrl: './global-context.component.html',
  styleUrls: ['./global-context.component.css']
})
export class GlobalContextComponent implements OnInit, OnDestroy {
  protected organizations: mjBizAppsCommonOrganizationEntity[] = [];
  protected channels: BCSaaSChannelEntity[] = [];
  protected selectedOrg: mjBizAppsCommonOrganizationEntity | null = null;
  protected selectedChannel: BCSaaSChannelEntity | null = null;
  protected isOpen = false;
  protected isReady = false;

  private destroy$ = new Subject<void>();

  constructor(private contextService: IzzyContextService) {}

  async ngOnInit(): Promise<void> {
    // Mirror section-driven changes into the header display
    this.contextService.orgChanges$
      .pipe(takeUntil(this.destroy$))
      .subscribe(id => this.applyOrg(id, false));
    this.contextService.channelChanges$
      .pipe(takeUntil(this.destroy$))
      .subscribe(id => this.applyChannel(id, false));

    // The app root creates this component at boot, BEFORE auth completes and the metadata engine
    // has data (sections init later, post-router, so they never hit this). Wait for the login
    // event rather than polling — calling Config() pre-auth throws inside RunViews because no data
    // provider is registered yet, and MJ logs that before it can be caught here.
    // withReplay=true so this still fires if login completed before we subscribed.
    MJGlobal.Instance.GetEventListener(true)
      .pipe(filter(e => e.event === MJEventType.LoggedIn), take(1), takeUntil(this.destroy$))
      .subscribe(() => { void this.load(); });
  }

  private async load(): Promise<void> {
    if (this.isReady) return;
    try {
      // Startup-registered engines populate during Startup(); reading engine data before it
      // resolves finds nothing.
      await StartupManager.Instance.Startup();

      const engine = IzzyMetadataEngine.Instance;
      await engine.Config(false);
      const contactId = engine.CurrentContactID;
      const orgs = contactId ? engine.GetUserOrganizations(contactId) : [];
      if (orgs.length === 0) return; // no orgs for this user — nothing to switch between

      this.organizations = orgs;

      // Initialize from whatever context a section already restored; otherwise
      // default to the first org and write it so sections pick it up.
      this.applyOrg(this.contextService.selectedOrganizationId, false);
      if (!this.selectedOrg) {
        this.applyOrg(this.organizations[0].ID, true);
      }
      this.applyChannel(this.contextService.selectedChannelId, !this.contextService.selectedChannelId);
      this.isReady = true;
    } catch (error) {
      // Post-login failures are real (bad metadata, dropped connection), not the pre-auth race
      // this used to swallow — surface them instead of leaving a silently empty switcher.
      console.error('GlobalContext: failed to load organizations/channels', error);
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  protected get orgInitials(): string {
    return (this.selectedOrg?.Name ?? '??').slice(0, 2).toUpperCase();
  }

  protected initialsFor(name: string): string {
    return (name ?? '?').slice(0, 2).toUpperCase();
  }

  protected toggle(event: MouseEvent): void {
    event.stopPropagation();
    this.isOpen = !this.isOpen;
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    const target = event.target as HTMLElement;
    if (!target.closest('izzy-global-context')) {
      this.isOpen = false;
    }
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    this.isOpen = false;
  }

  protected selectOrg(org: mjBizAppsCommonOrganizationEntity): void {
    if (UUIDsEqual(org.ID, this.selectedOrg?.ID ?? null)) return;
    this.applyOrg(org.ID, true);
    // Org changed — move to its first channel so the pair stays coherent
    const first = this.channels[0] ?? null;
    this.selectedChannel = first;
    this.contextService.selectedChannelId = first?.ID ?? null;
  }

  protected selectChannel(channel: BCSaaSChannelEntity): void {
    this.selectedChannel = channel;
    this.contextService.selectedChannelId = channel.ID;
    this.isOpen = false;
  }

  /** Sync org display (and channel list) from an ID; optionally write the service. */
  private applyOrg(orgId: string | null, write: boolean): void {
    const engine = IzzyMetadataEngine.Instance;
    this.selectedOrg = orgId
      ? this.organizations.find(o => UUIDsEqual(o.ID, orgId)) ?? this.selectedOrg
      : this.selectedOrg;
    const id = this.selectedOrg?.ID ?? null;
    this.channels = id
      ? engine.Channels.filter(c => UUIDsEqual(c.OrganizationID, id))
      : [];
    if (write && id) {
      this.contextService.selectedOrganizationId = id;
    }
    // Selected channel may no longer belong to this org
    if (this.selectedChannel && !this.channels.some(c => UUIDsEqual(c.ID, this.selectedChannel!.ID))) {
      this.selectedChannel = null;
    }
  }

  private applyChannel(channelId: string | null, writeDefault: boolean): void {
    if (channelId) {
      this.selectedChannel = this.channels.find(c => UUIDsEqual(c.ID, channelId)) ?? this.selectedChannel;
    } else if (writeDefault && this.channels.length > 0) {
      this.selectedChannel = this.channels[0];
      this.contextService.selectedChannelId = this.selectedChannel.ID;
    }
  }
}
